import { Factory } from '..'
import { Identifiable } from '../interface/identifiable'
import { Modifiable } from '../interface/modifiable'
import { applyEntitySchema, objectSchema, optionalTolerant, sourceJson } from '../../_utils/fcdc-v2'
import * as z from 'zod'

const dateValue = optionalTolerant(z.union([z.string(), z.number()]))

const versionSchema = objectSchema({
  id: optionalTolerant(z.string()),
  at: dateValue,
})

const manifestSchema = objectSchema({
  versionId: optionalTolerant(z.string()),
  firmwareHash: optionalTolerant(z.string()),
  size: optionalTolerant(z.number()),
  description: optionalTolerant(z.string()),
  timestamp: dateValue,
})

const firmwareSchema = objectSchema({
  id: optionalTolerant(z.string()),
  actual: optionalTolerant(z.unknown()),
  update: optionalTolerant(z.unknown()),
  modified: dateValue,
  version: optionalTolerant(z.string()),
  buildDate: dateValue,
  buildNumber: optionalTolerant(z.string()),
  buildType: optionalTolerant(z.string()),
  buildHash: optionalTolerant(z.string()),
})

export class Version {
  id: string
  at: Date

  constructor(json: any) {
    const source = sourceJson(json) as any
    this.id = source.id
    this.at = new Date(source.at)
    applyEntitySchema(this, versionSchema, source, {})
  }
}

export class Manifest {
  versionId: string
  firmwareHash: string
  size: number
  description?: string
  timestamp: Date

  constructor(json: any) {
    const source = sourceJson(json) as any
    this.versionId = source.versionId
    this.firmwareHash = source.firmwareHash
    this.size = source.size
    this.description = source.description
    this.timestamp = new Date(source.timestamp)
    applyEntitySchema(this, manifestSchema, source, {})
  }
}

export enum UpdateError {
  Unknown = 'Unknown',
  NetworkError = 'NetworkError',
  SignatureMismatch = 'SignatureMismatch',
  HashMismatch = 'HashMismatch',
  FlashWriteFailure = 'FlashWriteFailure',
  InsufficientStorage = 'InsufficientStorage',
  NoConfirmation = 'NoConfirmation',
  ApplicationValidationFailure = 'ApplicationValidationFailure',
}

export interface PendingUpdate {
  status: 'Pending'
  versionId: string
  at: Date
}

export interface FailureUpdate {
  status: 'Failure'
  versionId: string
  error: UpdateError
  message?: string
  at: Date
}

export interface RollbackUpdate {
  status: 'Rollback'
  versionId: string
  at: Date
}

export type Update = PendingUpdate | FailureUpdate | RollbackUpdate

export class Firmware implements Identifiable, Modifiable {
  id: string
  actual?: Version
  update?: Update
  modified: Date
  version: string
  buildDate: Date
  buildNumber: string
  buildType: string
  buildHash: string

  constructor(json: any) {
    const source = sourceJson(json) as Record<string, unknown>
    const actual =
      source['actual'] && typeof source['actual'] === 'object'
        ? sourceJson(source['actual'])
        : undefined
    const update =
      source['update'] && typeof source['update'] === 'object'
        ? sourceJson(source['update'])
        : undefined
    const plain: Record<string, unknown> = { ...source }
    if (actual) {
      plain['actual'] = actual
    }
    if (update) {
      plain['update'] = update
    }

    this.id = source['id'] as string
    this.actual = actual ? new Version(actual) : undefined
    this.update = update ? this.parseUpdate(update) : undefined
    this.modified = new Date(source['modified'] as string)
    this.version = (source['version'] as string) ?? ''
    this.buildDate = source['buildDate'] ? new Date(source['buildDate'] as string) : new Date()
    this.buildNumber = (source['buildNumber'] as string) ?? ''
    this.buildType = (source['buildType'] as string) ?? ''
    this.buildHash = (source['buildHash'] as string) ?? ''
    applyEntitySchema(this, firmwareSchema, plain, {})
  }

  private parseUpdate(json: any): Update {
    switch (json.status) {
      case 'Pending':
        return {
          status: 'Pending',
          versionId: json.versionId,
          at: new Date(json.at),
        }
      case 'Failure':
        return {
          status: 'Failure',
          versionId: json.versionId,
          error: json.error as UpdateError,
          message: json.message,
          at: new Date(json.at),
        }
      case 'Rollback':
        return {
          status: 'Rollback',
          versionId: json.versionId,
          at: new Date(json.at),
        }
      default:
        return { ...(json ?? {}) } as Update
    }
  }

  static getFactory(): Factory<Firmware> {
    return new (class implements Factory<Firmware> {
      make(json: any): Firmware {
        return new Firmware(json)
      }

      getTableName(): string {
        return 'firmware'
      }
    })()
  }

  static getUrl(columnId: string): string {
    return `/columns/${columnId}/firmware`
  }
}
